import './style.scss';
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';

export function map_block() {
    const containers = document.querySelectorAll(
        '.map_block .map-container'
    );
    
    if (!containers.length) return;
    
    containers.forEach((container) => {
        const tokenB64 = (
            container.getAttribute('data-mapbox-access-token') || ''
        ).trim();
        
        const latRaw = (
            container.getAttribute('data-latitude') || ''
        ).trim();
        
        const lngRaw = (
            container.getAttribute('data-longitude') || ''
        ).trim();
        
        let accessToken = '';
        
        try {
            accessToken = JSON.parse(atob(tokenB64));
        } catch (error) {
            console.error('Invalid Mapbox access token.');
            return;
        }
        
        const lat = parseFloat(latRaw);
        const lng = parseFloat(lngRaw);
        
        if (
            !accessToken ||
            Number.isNaN(lat) ||
            Number.isNaN(lng)
        ) {
            return;
        }
        
        mapboxgl.accessToken = accessToken;
        
        const mapEl =
            container.querySelector('#map') ||
            container.querySelector('.map-block.map-image-container') ||
            container;
        
        if (mapEl.dataset.mapInitialized === 'true') {
            return;
        }
        
        mapEl.dataset.mapInitialized = 'true';
        
        const DEFAULT_ZOOM = 13;
        const LOCATION_ZOOM = 16;
        const ZOOM_THRESHOLD = 13.25;
        
        const map = new mapboxgl.Map({
            container: mapEl,
            style: 'mapbox://styles/mapbox/streets-v12',
            center: [lng, lat],
            zoom: DEFAULT_ZOOM,
            attributionControl: true,
            scrollZoom: false,
        });
        
        map.addControl(
            new mapboxgl.NavigationControl({
                showCompass: false,
            }),
            'top-right'
        );
        
        /*
         * Plus marker
         *
         * The wrapper is controlled by Mapbox.
         * The button inside it is controlled by our CSS.
         */
        const plusMarkerElement = document.createElement('div');
        plusMarkerElement.className = 'location-plus-marker';
        
        const plusButton = document.createElement('button');
        plusButton.type = 'button';
        plusButton.className = 'location-plus-marker__button';
        plusButton.setAttribute('aria-label', 'Zoom to location');
        
        const plusIcon = document.createElement('span');
        plusIcon.className = 'location-plus-marker__icon';
        
        plusButton.appendChild(plusIcon);
        plusMarkerElement.appendChild(plusButton);
        
        new mapboxgl.Marker({
            element: plusMarkerElement,
            anchor: 'center',
        })
            .setLngLat([lng, lat])
            .addTo(map);
        
        /*
         * Normal Mapbox pin marker
         */
        const pinMarker = new mapboxgl.Marker({
            color: '#28a9c9',
            anchor: 'bottom',
        })
            .setLngLat([lng, lat])
            .addTo(map);
        
        const pinMarkerElement = pinMarker.getElement();
        const pinVisual = pinMarkerElement.querySelector('svg');
        
        pinMarkerElement.classList.add('location-pin-marker');
        
        if (pinVisual) {
            pinVisual.classList.add('location-pin-marker__visual');
        }
        
        let currentMode = null;
        let isFlyingToLocation = false;
        
        /**
         * Show the circle with plus.
         * Hide the normal pin.
         */
        const showPlusMarker = () => {
            if (currentMode === 'plus') return;
            
            currentMode = 'plus';
            
            plusButton.classList.remove('is-hidden');
            plusButton.classList.add('is-visible');
            
            if (pinVisual) {
                pinVisual.classList.remove('is-visible');
                pinVisual.classList.add('is-hidden');
            }
            
            plusMarkerElement.style.pointerEvents = 'auto';
            pinMarkerElement.style.pointerEvents = 'none';
            
            plusButton.disabled = false;
            plusButton.tabIndex = 0;
            
            plusButton.setAttribute('aria-hidden', 'false');
            pinMarkerElement.setAttribute('aria-hidden', 'true');
        };
        
        /**
         * Show the normal pin.
         * Hide the circle with plus.
         */
        const showPinMarker = () => {
            if (currentMode === 'pin') return;
            
            currentMode = 'pin';
            
            plusButton.classList.remove('is-visible');
            plusButton.classList.add('is-hidden');
            
            if (pinVisual) {
                pinVisual.classList.remove('is-hidden');
                pinVisual.classList.add('is-visible');
            }
            
            plusMarkerElement.style.pointerEvents = 'none';
            pinMarkerElement.style.pointerEvents = 'auto';
            
            plusButton.disabled = true;
            plusButton.tabIndex = -1;
            
            plusButton.setAttribute('aria-hidden', 'true');
            pinMarkerElement.setAttribute('aria-hidden', 'false');
        };
        
        /**
         * Switch marker depending on map zoom.
         *
         * This detects:
         * - Custom plus button
         * - Mapbox navigation controls
         * - Mouse zoom
         * - Touch zoom
         */
        const updateMarkerByZoom = () => {
            const currentZoom = map.getZoom();
            
            if (
                isFlyingToLocation ||
                currentZoom > ZOOM_THRESHOLD
            ) {
                showPinMarker();
            } else {
                showPlusMarker();
            }
        };
        
        /**
         * Custom plus button click
         */
        plusButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            
            isFlyingToLocation = true;
            
            // Switch immediately before zoom animation
            showPinMarker();
            
            map.flyTo({
                center: [lng, lat],
                zoom: LOCATION_ZOOM,
                speed: 1.2,
                curve: 1.4,
                essential: true,
            });
        });
        
        /*
         * Detect every zoom change, including Mapbox controls.
         */
        map.on('zoom', updateMarkerByZoom);
        
        /*
         * Finish the custom flyTo state.
         */
        map.on('moveend', () => {
            isFlyingToLocation = false;
            updateMarkerByZoom();
        });
        
        map.once('load', () => {
            map.resize();
            
            // Initial state:
            // plus visible and pin hidden
            showPlusMarker();
        });
        
        setTimeout(() => {
            map.resize();
        }, 150);
    });
}